import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../lib/api';
import './ReportProblem.css';

const ReportProblem: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    subject: '',
    category: 'bug',
    description: '',
    priority: 'medium'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit report to API
      const response = await apiClient.reports.create(formData);
      
      console.log('Problem report submitted successfully:', response);
      setIsSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          subject: '',
          category: 'bug',
          description: '',
          priority: 'medium'
        });
      }, 3000);
      
    } catch (error: any) {
      console.error('Error submitting problem report:', error);
      const errorMessage = error.response?.data?.detail || 'There was an error submitting your report. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="report-problem-container">
        <div className="success-message">
          <div className="success-icon">✅</div>
          <h2>Report Submitted Successfully!</h2>
          <p>Thank you for reporting this issue. Our team will review it and get back to you as soon as possible.</p>
          <button 
            className="back-to-dashboard-btn"
            onClick={() => navigate('/')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="report-problem-container">
      <div className="report-problem-header">
        <h1>Support</h1>
        <p>Need help or found a bug? Tell us what's going on and we'll get back to you.</p>
      </div>

      <form onSubmit={handleSubmit} className="report-problem-form">
        <div className="form-section">
          <h3>Issue Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="ui">UI/UX Issue</option>
                <option value="performance">Performance Issue</option>
                <option value="data">Data Issue</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="priority">Priority</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                <option value="low">Low - Minor inconvenience</option>
                <option value="medium">Medium - Moderate impact</option>
                <option value="high">High - Significant impact</option>
                <option value="critical">Critical - Platform unusable</option>
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="subject">Subject *</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              placeholder="Brief description of the issue"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={6}
              placeholder="Please provide detailed information about the issue, including steps to reproduce it..."
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="cancel-btn"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="submit-btn"
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReportProblem;
